import { computed, reactive, ref } from 'vue';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { ORDER_BADGE_KEYWORDS, keywordMatches, shouldSkipOrderForBadge } from '@/lib/orderBadgeKeywords';

const parseOrderDate = (order) => {
  const candidates = [order?.orderDate, order?.importedAt, order?.updatedAt, order?.createdAt];
  for (const value of candidates) {
    if (!value) continue;
    if (typeof value?.toDate === 'function') {
      const date = value.toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) {
        return date;
      }
    }
    const str = String(value).replace(' ', 'T');
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
};

const formatOrderDate = (order) => {
  const date = parseOrderDate(order);
  if (!date) return 'Data n/d';
  return date.toLocaleDateString('it-IT', { dateStyle: 'medium' });
};

const formatOrderPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const formatStatus = (status) => {
  if (!status) return '—';
  return String(status)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const highlightKeysForUser = (user) => {
  const keys = [];
  if (user?.uid) keys.push(user.uid);
  const emailKey = user?.email ? String(user.email).trim() : '';
  if (emailKey) keys.push(emailKey);
  return keys;
};

export const useOrderBadge = () => {
  const db = getFirestore();
  const showOrderModal = ref(false);
  const orderLoading = ref(false);
  const orderError = ref('');
  const selectedOrder = ref(null);
  const orderHighlights = reactive({});

  const fetchLatestOrderByEmail = async (email) => {
    const trimmedEmail = String(email || '').trim();
    if (!trimmedEmail) return null;

    const ordersRef = collection(db, 'orders');
    const snap = await getDocs(query(ordersRef, where('userEmail', '==', trimmedEmail)));
    if (snap.empty) return null;

    const sorted = snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .sort((a, b) => {
        const bTime = parseOrderDate(b)?.getTime() ?? 0;
        const aTime = parseOrderDate(a)?.getTime() ?? 0;
        return bTime - aTime;
      });

    const latestValid = sorted.find((order) => !shouldSkipOrderForBadge(order));
    return latestValid || null;
  };

  const categorizeOrder = (order) => {
    if (!order || shouldSkipOrderForBadge(order)) return 'neutral';
    const productsArray = Array.isArray(order.products) ? order.products : [];
    const names = productsArray
      .map((item) => item?.name || item?.title || item?.productName || item?.sku)
      .filter(Boolean)
      .map((name) => String(name).toLowerCase());

    if (keywordMatches(names, ORDER_BADGE_KEYWORDS.green)) {
      return 'green';
    }

    if (keywordMatches(names, ORDER_BADGE_KEYWORDS.orange)) {
      return 'orange';
    }

    return 'neutral';
  };

  const updateHighlight = (keys, status) => {
    keys.forEach((key) => {
      if (!key) return;
      if (status && status !== 'neutral') {
        orderHighlights[key] = { status };
      } else if (orderHighlights[key]) {
        delete orderHighlights[key];
      }
    });
  };

  const productHighlightClass = (productName = '') => {
    const normalized = String(productName).toLowerCase();
    if (keywordMatches([normalized], ORDER_BADGE_KEYWORDS.green)) {
      return 'order-product--green';
    }
    if (keywordMatches([normalized], ORDER_BADGE_KEYWORDS.orange)) {
      return 'order-product--orange';
    }
    return 'order-product--neutral';
  };

  const orderBadgeStatus = (user) => {
    const keys = highlightKeysForUser(user);
    return keys.map((key) => orderHighlights[key]?.status).find((status) => !!status) || 'neutral';
  };

  const prefetchUserHighlights = async (userList = []) => {
    const processedEmails = new Set();
    for (const user of userList) {
      const email = String(user?.email || '').trim();
      if (!email || processedEmails.has(email)) continue;
      processedEmails.add(email);

      try {
        const latest = await fetchLatestOrderByEmail(email);
        updateHighlight(highlightKeysForUser(user), categorizeOrder(latest));
      } catch (error) {
        console.error('Errore nel prefetch del colore ordine:', error);
        updateHighlight(highlightKeysForUser(user), 'neutral');
      }
    }
  };

  const openLastOrder = async (user) => {
    const highlightKeys = highlightKeysForUser(user);

    if (!user?.email) {
      orderError.value = 'Email non disponibile per questo utente.';
      selectedOrder.value = null;
      showOrderModal.value = true;
      updateHighlight(highlightKeys, false);
      return;
    }

    orderLoading.value = true;
    orderError.value = '';
    selectedOrder.value = null;
    showOrderModal.value = true;

    try {
      const latest = await fetchLatestOrderByEmail(user.email);
      selectedOrder.value = latest || null;
      updateHighlight(highlightKeys, categorizeOrder(latest));
    } catch (error) {
      console.error('Errore recuperando ultimo ordine:', error);
      orderError.value = 'Impossibile recuperare gli ordini.';
      selectedOrder.value = null;
      updateHighlight(highlightKeys, 'neutral');
    } finally {
      orderLoading.value = false;
    }
  };

  const closeOrderModal = () => {
    showOrderModal.value = false;
    selectedOrder.value = null;
    orderError.value = '';
  };

  const orderSummary = computed(() => {
    if (!selectedOrder.value) return null;
    const order = selectedOrder.value;
    const productsArray = Array.isArray(order.products) ? order.products : [];
    const productNames = productsArray
      .map((item) => item?.name || item?.title || item?.productName || item?.sku)
      .filter(Boolean);

    return {
      code: order.orderId || order._id || order.id || '—',
      date: formatOrderDate(order),
      total: formatOrderPrice(order.totalPrice ?? order.total ?? order.amount),
      status: formatStatus(order.status || order.paymentStatus || order.state),
      products: productNames,
    };
  });

  return {
    showOrderModal,
    orderLoading,
    orderError,
    selectedOrder,
    orderSummary,
    openLastOrder,
    closeOrderModal,
    orderBadgeStatus,
    productHighlightClass,
    prefetchUserHighlights,
    fetchLatestOrderByEmail,
    categorizeOrder,
  };
};

export const orderBadgeUtils = {
  parseOrderDate,
  formatOrderDate,
  formatOrderPrice,
  formatStatus,
};
